<!--@format-->
<template>
    <div class="campaign-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ isLoading ? 'Loading...' : currentStep.title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else class="widget-container">
                    <campaign-widget-strategy-step
                        v-if="currentStep === steps.strategy"
                        ref="strategyStep"
                        @nextStep="goToStep('campaignSettings')"
                    />

                    <campaign-widget-settings-step
                        v-if="currentStep === steps.campaignSettings"
                        ref="settingsStep"
                        @closeWidget="closeWidget($event)"
                        @nextStep="goToStep('list')"
                    />

                    <campaign-widget-list-step
                        v-if="currentStep === steps.list"
                        ref="listStep"
                        @closeWidget="closeWidget($event)"
                        @nextStep="goToNextStepAfterList()"
                    />

                    <campaign-widget-ab-distribution-step
                        v-if="currentStep === steps.abDistribution"
                        ref="abDistribution"
                        @nextStep="goToStep('review')"
                    />

                    <campaign-widget-review-step
                        v-if="currentStep === steps.review"
                        ref="reviewStep"
                        @nextStep="goToStep('schedule')"
                        @goToStep="goToStep($event)"
                        @closeWidget="closeWidget()"
                        @unschedule="unscheduleCampaign()"
                    />

                    <campaign-widget-schedule-step
                        v-if="currentStep === steps.schedule"
                        ref="scheduleStep"
                        @nextStep="closeWidget()"
                        @campaign:edit="goToStep($event)"
                    />
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div v-if="!isLoading && !campaign.isScheduled" class="action-container">
                    <app-button
                        v-if="currentStep != steps['strategy']"
                        class="back-btn"
                        @click="goToPreviousStep"
                    >
                        Back
                    </app-button>
                    <app-button class="save-btn" @click="currentStep.action">
                        {{ currentStep.buttonText }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const CampaignWidgetStrategyStep = () =>
    import('@/components/sliding-widgets/campaign-widget/campaign-widget-strategy-step');
const CampaignWidgetSettingsStep = () =>
    import('@/components/sliding-widgets/campaign-widget/campaign-widget-settings-step');
const CampaignWidgetListStep = () =>
    import('@/components/sliding-widgets/campaign-widget/campaign-widget-list-step');
const CampaignWidgetAbDistributionStep = () =>
    import('@/components/sliding-widgets/campaign-widget/campaign-widget-ab-distribution-step');
const CampaignWidgetReviewStep = () =>
    import('@/components/sliding-widgets/campaign-widget/campaign-widget-review-step');
const CampaignWidgetScheduleStep = () =>
    import('@/components/sliding-widgets/campaign-widget/campaign-widget-schedule-step');

import EventBus from '@/scripts/event-bus.js';
import { required, email, number } from 'vuelidate/lib/validators';

export default {
    name: 'campaign-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'campaign-widget-strategy-step': CampaignWidgetStrategyStep,
        'campaign-widget-settings-step': CampaignWidgetSettingsStep,
        'campaign-widget-list-step': CampaignWidgetListStep,
        'campaign-widget-ab-distribution-step': CampaignWidgetAbDistributionStep,
        'campaign-widget-review-step': CampaignWidgetReviewStep,
        'campaign-widget-schedule-step': CampaignWidgetScheduleStep,
    },

    provide() {
        return {
            campaign: this.campaign,
            client: this.client,
            marketingPlan: this.marketingPlan,
        };
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: true,

            steps: {
                strategy: {
                    title: 'Campaign Type',
                    buttonText: 'Next',
                    loading: false,
                    action: () => {
                        this.$refs.strategyStep.next();
                    },
                },
                campaignSettings: {
                    title: 'Campaign Settings',
                    buttonText: 'Save & Continue',
                    loading: false,
                    action: () => {
                        this.$refs.settingsStep.next();
                    },
                },
                list: {
                    title: 'Lists',
                    buttonText: 'Save & Continue',
                    loading: false,
                    action: () => {
                        this.$refs.listStep.next();
                    },
                },
                abDistribution: {
                    title: 'A/B Split',
                    buttonText: 'Save & Continue',
                    loading: false,
                    action: () => {
                        this.$refs.abDistribution.patchCampaign();
                    },
                },
                review: {
                    title: 'Review',
                    buttonText: 'Save & Continue',
                    loading: false,
                    action: () => {
                        this.$refs.reviewStep.next();
                    },
                },
                schedule: {
                    title: 'Schedule',
                    buttonText: 'Schedule',
                    loading: false,
                    action: () => {
                        this.$refs.scheduleStep.patchCampaign();
                    },
                },
            },

            currentStep: null,

            campaign: {
                id: this.widget.campaignId,
                clientId: this.widget.clientId,
                testType: null,
                isPlain: false,
                hasAbTest: false,
                hasSent: false,
                name: null,
                fromName: null,
                fromEmail: null,
                subjectA: null,
                subjectB: null,
                lists: [],
                testMetric: null,
                testedContactsPercentage: 0,
                duration: 0,
                unit: null,
                sendDate: null,
                uniqueContactCount: 0,
                timezone: this.$helpers.guessTimezone().name,
                previewImageUrl: null,
                templateId: this.widget.templateId,
                emailTemplateId: this.widget.emailTemplateId,
                configurations: {
                    configurationA: null,
                    configurationB: null,
                },
            },

            client: {
                id: this.widget.clientId,
                name: null,
                logoImageUrl: null,
                footer: null,
            },

            marketingPlan: {
                id: this.widget.marketingPlanId,
                lists: [],
                uniqueContactCount: 0,
            },
        };
    },

    created() {
        if (this.campaign.id) {
            this.getCampaign().then(() => {
                if (this.widget.step) {
                    this.goToStep(this.widget.step);
                } else {
                    var missingSettings =
                        !this.campaign.name ||
                        !this.campaign.subjectA ||
                        !this.campaign.fromName ||
                        !this.campaign.fromEmail ||
                        !this.campaign.configurations.configurationA;
                    var missingLists = this.campaign.lists.length === 0;
                    var missingABSubjectSettings =
                        this.campaign.hasAbTest &&
                        this.campaign.testType == 'Subject' &&
                        !this.campaign.subjectB;
                    var missingABContentSettings =
                        this.campaign.hasAbTest &&
                        this.campaign.testType == 'Content' &&
                        !this.campaign.configurations.configurationB;
                    var missingABSettings =
                        this.campaign.hasAbTest &&
                        (!this.campaign.testedContactsPercentage ||
                            !this.campaign.duration ||
                            !this.campaign.testMetric);
                    var missingScheduleSettings = !this.campaign.scheduledSendDate;
                    var readyForReview =
                        this.campaign.name &&
                        this.campaign.subjectA &&
                        this.campaign.fromName &&
                        this.campaign.fromEmail &&
                        this.campaign.configurations.configurationA &&
                        (this.campaign.lists.length > 0 || this.marketingPlan.id);
                    var readyForReviewSubjectTesting =
                        this.campaign.testType == 'Subject' &&
                        readyForReview &&
                        this.campaign.subjectB &&
                        !missingABSettings &&
                        this.campaign.duration &&
                        this.campaign.unit;
                    var readyForReviewContentTesting =
                        this.campaign.testType == 'Content' &&
                        readyForReview &&
                        this.campaign.configurations.configurationB &&
                        this.campaign.duration &&
                        this.campaign.unit;

                    if (
                        readyForReviewSubjectTesting ||
                        readyForReviewContentTesting ||
                        (readyForReview && !this.campaign.hasAbTest)
                    ) {
                        this.goToStep('review');
                    } else if (
                        missingABSubjectSettings ||
                        missingABContentSettings ||
                        missingSettings ||
                        missingLists
                    ) {
                        this.goToStep('campaignSettings');
                    } else if (missingABSettings) {
                        this.goToStep('abDistribution');
                    } else if (missingScheduleSettings) {
                        this.goToStep('review');
                    } else {
                        this.goToStep('strategy');
                    }
                }
                this.isLoading = false;
            });
        } else {
            this.campaign.sendDate = this.widget.sendDate;

            this.getClient()
                .then(() => {
                    var promises = [];

                    if (this.marketingPlan.id) {
                        promises.push(this.hydrateMarketingPlan());
                    }

                    if (this.campaign.emailTemplateId || this.campaign.templateId) {
                        promises.push(this.adopt());
                    }

                    if (this.widget.listId) {
                        this.getInitialList(this.widget.listId);
                    }

                    return Promise.all(promises);
                })
                .then(() => {
                    this.goToStep('strategy');
                    this.isLoading = false;
                });
        }

        EventBus.$on('campaign-configuration:update', this.assignCampaignConfiguration);
    },

    beforeDestroy() {
        EventBus.$off('campaign-configuration:update', this.assignCampaignConfiguration);
    },

    methods: {
        assignCampaignConfiguration(configuration, version) {
            if (version === 'B') {
                this.campaign.configurations.configurationB = configuration;
            } else {
                this.campaign.configurations.configurationA = configuration;
            }
        },

        goToStep(step) {
            this.currentStep = this.steps[step];
        },

        goToPreviousStep() {
            if (this.currentStep === this.steps.campaignSettings) {
                this.goToStep('strategy');
            } else if (this.currentStep === this.steps.list) {
                this.goToStep('campaignSettings');
            } else if (this.currentStep === this.steps.abDistribution) {
                this.goToStep('list');
            } else if (this.currentStep === this.steps.review) {
                if (!this.campaign.hasAbTest) {
                    this.goToStep('list');
                } else {
                    this.goToStep('abDistribution');
                }
            } else if (this.currentStep === this.steps.schedule) {
                this.goToStep('review');
            }
        },

        goToNextStepAfterList() {
            if (this.campaign.hasAbTest) {
                this.goToStep('abDistribution');
            } else {
                this.goToStep('review');
            }
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getCampaign() {
            return new Promise((resolve, reject) => {
                var select = `$select=ClientId,TemplateId,EmailTemplateId,IsPlain,HasAbTest,TestType,Name,SubjectA,SubjectB,FromEmail,FromName,TestDurationAmount,TestPercent,TestMetric,TestDurationType,ConfigurationA,ConfigurationB,TotalRecipients,Timezone,PreviewImageUrl,MarketingPlanCampaigns,DateSent,IsScheduled,ScheduledSendDate`;
                this.$api
                    .get(
                        `/service/Campaigns/?$filter=Id eq ${this.campaign.id}&$expand=MarketingPlanCampaigns&${select}`
                    )
                    .then((response) => {
                        var responseValue = response.data.value[0];

                        this.campaign.clientId = responseValue.ClientId;

                        let clientPromise = Promise.resolve();
                        if (!this.clientId) {
                            this.client.id = responseValue.ClientId;
                            clientPromise = this.getClient();
                        }

                        clientPromise
                            .then(() => {
                                this.campaign.templateId = responseValue.TemplateId;
                                this.campaign.emailTemplateId = responseValue.EmailTemplateId;
                                this.campaign.isPlain = responseValue.IsPlain;
                                this.campaign.hasAbTest = responseValue.HasAbTest;
                                this.campaign.testType = responseValue.TestType;
                                this.campaign.name = responseValue.Name;
                                this.campaign.subjectA = responseValue.SubjectA;
                                this.campaign.subjectB = responseValue.SubjectB;
                                this.campaign.fromEmail = responseValue.FromEmail;
                                this.campaign.fromName = responseValue.FromName;
                                this.campaign.duration = responseValue.TestDurationAmount;
                                this.campaign.testedContactsPercentage = responseValue.TestPercent;
                                this.campaign.testMetric = responseValue.TestMetric;
                                this.campaign.unit = responseValue.TestDurationType;
                                this.campaign.configurations.configurationA = JSON.parse(
                                    responseValue.ConfigurationA
                                );
                                this.campaign.configurations.configurationB = JSON.parse(
                                    responseValue.ConfigurationB
                                );
                                this.campaign.uniqueContactCount = responseValue.TotalRecipients || 0;
                                this.campaign.timezone = responseValue.Timezone || this.campaign.timezone;
                                this.campaign.previewImageUrl = responseValue.PreviewImageUrl;
                                this.campaign.isScheduled = responseValue.IsScheduled;
                                this.campaign.hasSent = responseValue.DateSent ? true : false;

                                if (responseValue.ScheduledSendDate) {
                                    if (this.campaign.timezone) {
                                        this.campaign.sendDate = this.$moment(
                                            responseValue.ScheduledSendDate
                                        ).tz(this.campaign.timezone);
                                    } else {
                                        this.campaign.sendDate = this.$moment(
                                            responseValue.ScheduledSendDate
                                        );
                                    }
                                } else {
                                    this.campaign.sendDate = null;
                                }

                                var marketingPlanCampaigns = responseValue.MarketingPlanCampaigns || [];

                                if (!this.campaign.hasSent && marketingPlanCampaigns.length > 0) {
                                    this.marketingPlan.id = marketingPlanCampaigns[0].MarketingPlanId;
                                    this.hydrateMarketingPlan().then(resolve);
                                } else {
                                    this.getCampaignLists().then(resolve);
                                }
                            });    
                    })
                    .catch(reject);
            });
        },

        hydrateMarketingPlan() {
            return new Promise((resolve, reject) => {
                var select = `$select=FromName,FromEmail,Name`;
                this.$api
                    .get(`/service/MarketingPlans?$filter=Id eq ${this.marketingPlan.id}&${select}`)
                    .then((response) => {
                        var marketingPlan = response.data.value[0];

                        if (!this.campaign.id) {
                            this.campaign.fromName = marketingPlan.FromName;
                            this.campaign.fromEmail = marketingPlan.FromEmail;
                        }

                        this.marketingPlan.name = marketingPlan.Name;
                    })
                    .then(this.getMarketingPlanLists)
                    .then(this.getUniqueContactCount)
                    .then(resolve)
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while getting your marketing plan.',
                            { error },
                        );
                    });
            });
        },

        getMarketingPlanLists() {
            return new Promise((resolve, reject) => {
                var select = `$select=Name,Id`;
                this.$api
                    .get(
                        `/service/Lists?$expand=ListContacts($count=true;$top=0)&$filter=MarketingPlanLists/any(l: l/MarketingPlanId eq ${this.marketingPlan.id})&${select}`
                    )
                    .then((response) => {
                        this.marketingPlan.lists = response.data.value.map((list) => {
                            return {
                                name: list.Name,
                                id: list.Id,
                                count: list['ListContacts@odata.count'],
                                isMarketable: list.IsMarketable,
                            };
                        });
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
            });
        },

        getCampaignLists() {
            return new Promise((resolve, reject) => {
                var select = `$select=Name,Id`;
                this.$api
                    .get(
                        `/service/Lists?$expand=ListContacts($count=true;$top=0)&$filter=CampaignLists/any(l: l/CampaignId eq ${this.campaign.id})&${select}`
                    )
                    .then((response) => {
                        this.campaign.lists = response.data.value.map((list) => {
                            return {
                                name: list.Name,
                                id: list.Id,
                                count: list['ListContacts@odata.count'],
                                isMarketable: list.IsMarketable,
                            };
                        });
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
            });
        },

        getInitialList(listId) {
            return new Promise((resolve, reject) => {
                var select = `$select=Name,Id`;
                this.$api
                    .get(`/service/Lists?$filter=Id eq ${listId}&${select}`)
                    .then((response) => {
                        this.campaign.lists = response.data.value.map((list) => {
                            return {
                                name: list.Name,
                                id: list.Id,
                                count: list['ListContacts@odata.count'],
                                isMarketable: list.IsMarketable,
                            };
                        });
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
            });
        },

        getUniqueContactCount() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/api/MarketingPlans/${this.marketingPlan.id}/Recipients/Count`)
                    .then((response) => {
                        this.marketingPlan.uniqueContactCount = response.data;
                        if (!this.campaign.hasSent) {
                            this.campaign.uniqueContactCount =
                                this.marketingPlan.uniqueContactCount;
                        }
                        resolve();
                    })
                    .catch((response) => {
                        reject();
                    });
            });
        },

        unscheduleCampaign() {
            this.isLoading = true;
            this.getCampaign().then(() => {
                this.goToStep('review');
                this.isLoading = false;
            });
        },

        adopt() {
            if (this.campaign.templateId) {
                return this.adoptTemplate();
            } else if (this.campaign.emailTemplateId) {
                return this.adoptEmailTemplate();
            }
        },

        adoptTemplate() {
            this.isLoading = true;

            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/service/Templates?$filter=Id eq ${this.campaign.templateId}`)
                    .then((response) => {
                        this.campaign.name = `${response.data.value[0].Name} (Created from template)`;
                        this.campaign.configurations.configurationA = JSON.parse(
                            response.data.value[0].Configuration
                        );
                        this.campaign.previewImageUrl = response.data.value[0].PreviewImageUrl;
                        this.campaign.isPlain = false;

                        var isShared = response.data.value[0].ClientId !== this.campaign.clientId;
                        if (isShared) {
                            this.campaign.configurations.configurationA.logo.image.url = this.client.logoImageUrl;
                            this.campaign.configurations.configurationA.logo.text.value = `<h1>${this.client.name}</h1>`;
                            this.campaign.configurations.configurationA.footer.value = this.client.footer;
                            this.calculateLogoDimensions();
                        }

                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while getting your campaign template.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        calculateLogoDimensions() {
            var self = this;

            var image = new Image();
            image.src = this.campaign.configurations.configurationA.logo.image.url;
            image.onload = function () {
                self.campaign.configurations.configurationA.logo.image.width = this.width;
                self.campaign.configurations.configurationA.logo.image.height = this.height;
            };
        },

        adoptEmailTemplate() {
            this.isLoading = true;

            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/service/EmailTemplates?$filter=Id eq ${this.campaign.emailTemplateId}`)
                    .then((response) => {
                        this.campaign.name = `${response.data.value[0].Name} (Created from template)`;
                        this.campaign.configurations.configurationA = {
                            body: response.data.value[0].Body,
                        };
                        this.campaign.isPlain = true;
                        this.campaign.subjectA = response.data.value[0].Subject;
                        this.campaign.previewImageUrl = response.data.value[0].PreviewImageUrl;
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while getting your campaign template.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        getClient() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Clients?$filter=Id eq ${this.client.id}&$select=Id,Name,TemplateLogoImageUrl,TemplateFooter`,
                    )
                    .then((response) => {
                        this.client.name = response.data.value[0].Name;
                        this.client.logoImageUrl = response.data.value[0].TemplateLogoImageUrl;
                        this.client.footer = response.data.value[0].TemplateFooter;

                        resolve();
                    })
                    .catch(reject);
            });
        },
    },
};
</script>

<style>
.campaign-widget .input-label {
    font-weight: 600;
    display: flex;
    align-items: center;
}

.campaign-widget .action-container {
    display: flex;
}

.campaign-widget .action-container .save-btn {
    width: 128px;
}

.campaign-widget .action-container .back-btn {
    width: 128px;
    background: transparent;
    color: #18aeff;
    margin-right: 10px;
}

.campaign-widget .action-container .back-btn:hover {
    background: #e1e0e5;
}

.campaign-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.campaign-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.campaign-widget .error {
    color: #ff0000;
    font-size: 14px;
}

.campaign-widget .app-input label {
    display: none;
}

.campaign-widget .error-message {
    display: none;
}

.campaign-widget .widget-contents-container .content-create {
    padding: 0;
}

.campaign-widget .content-create .app-text-editor-wrapper,
.campaign-widget .content-create .app-text-editor,
.campaign-widget .content-create .widget-container {
    height: 100%;
}

.campaign-widget .content-create .widget-container .controls {
    display: none;
}
</style>
